import { cloneDeep } from 'lodash-es';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { creditConfig } from '../../constants/credit';
import {
  CreditApplicationStatusResponse,
  CreditApplicationStatusType,
  CreditFormData,
  CreditFormSubVariant,
  CreditState,
  CreditStepKey,
} from '../../types/credit';

const initialState: CreditState = {
  stepper: {
    companyDetails: {},
    billingDetails: {},
    insuranceDetails: {},
    review: {},
  },
  creditApplicationStatus: {
    id: null,
    status: CreditApplicationStatusType.NOT_STARTED,
    credit_limit: null,
  },
  creditApplicationSubmissionDate: '',
  isInitialized: false,
  errorMessage: '',
};

const creditSlice = createSlice({
  name: 'credit',
  initialState,
  reducers: {
    saveCreditFormData: (
      state,
      action: PayloadAction<{
        stepKey: CreditStepKey;
        formData: Partial<CreditFormSubVariant>;
      }>,
    ) => {
      if (state.stepper[action.payload.stepKey]) {
        state.stepper[action.payload.stepKey].formData = cloneDeep(action.payload.formData);
        state.stepper[action.payload.stepKey].isComplete = true;
      }
    },
    updateCreditStepperProgress: (
      state,
      action: PayloadAction<{
        stepKey: CreditStepKey;
      }>,
    ) => {
      const index = creditConfig.stepperOrder.findIndex((e) => e === action.payload.stepKey);
      creditConfig.stepperOrder.forEach((_e, i) => {
        if (i < index) {
          state.stepper[creditConfig.stepperOrder[i]].isComplete = true;
        } else {
          state.stepper[creditConfig.stepperOrder[i]].isComplete = false;
        }
      });
    },
    updateStepperStatus: (
      state,
      action: PayloadAction<{
        status: CreditApplicationStatusType;
        currentEditStepKey: CreditStepKey | null;
      }>,
    ) => {
      const status = action.payload.status;
      const currentEditStepKey = action.payload.currentEditStepKey;

      state.stepper.companyDetails.isComplete =
        status === CreditApplicationStatusType.STEP1_SAVED ||
        status === CreditApplicationStatusType.STEP2_SAVED ||
        status === CreditApplicationStatusType.STEP3_SAVED ||
        status === CreditApplicationStatusType.SUBMITTED;

      state.stepper.billingDetails.isComplete =
        status === CreditApplicationStatusType.STEP2_SAVED ||
        status === CreditApplicationStatusType.STEP3_SAVED ||
        status === CreditApplicationStatusType.SUBMITTED;

      state.stepper.insuranceDetails.isComplete =
        status === CreditApplicationStatusType.STEP3_SAVED ||
        status === CreditApplicationStatusType.SUBMITTED;

      state.stepper.review.isComplete = status === CreditApplicationStatusType.SUBMITTED;

      if (currentEditStepKey) {
        const index = creditConfig.stepperOrder.findIndex((e) => e === currentEditStepKey);
        creditConfig.stepperOrder.forEach((_e, i) => {
          if (i < index) {
            state.stepper[creditConfig.stepperOrder[i]].isComplete = true;
          } else {
            state.stepper[creditConfig.stepperOrder[i]].isComplete = false;
          }
        });
      }
    },
    prefillCreditApplicationData: (state, action: PayloadAction<CreditFormData>) => {
      const formData = action.payload;
      state.stepper.companyDetails.formData = formData.companyDetails;
      state.stepper.billingDetails.formData = formData.billingDetails;
      state.stepper.insuranceDetails.formData = formData.insuranceDetails;
      state.isInitialized = true;
      state.creditApplicationSubmissionDate = formData.dateSubmitted;
      state.creditApplicationStatus = formData.creditApplicationStatus;
    },
    setCreditApplicationError: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    setCreditApplicationStatus: (state, action: PayloadAction<CreditApplicationStatusResponse>) => {
      state.creditApplicationStatus = action.payload;
    },
    clearCredit: () => {
      return { ...initialState };
    },
  },
});

export const {
  saveCreditFormData,
  updateCreditStepperProgress,
  updateStepperStatus,
  prefillCreditApplicationData,
  setCreditApplicationError,
  setCreditApplicationStatus,
  clearCredit,
} = creditSlice.actions;

export const fetchCreditData = createAction<{ editingStepKey: CreditStepKey | null }>(
  'credit/fetchCreditData',
);

export default creditSlice.reducer;
